
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

// Browser environment check for SSR safety
const isBrowser = typeof window !== 'undefined';

// Analytics event types for type safety
export const ANALYTICS_EVENT = {
  PAGE_VIEW: 'page_view',
  LOGIN: 'login',
  LOGOUT: 'logout',
  PURCHASE: 'purchase',
  SEARCH: 'search',
  BOOK_VIEW: 'book_view',
  BOOK_SAVE: 'book_save',
  ADD_TO_CART: 'add_to_cart',
  // Payment lifecycle events
  CHECKOUT_OPENED: 'checkout_opened',
  PAYMENT_SUCCESS: 'payment_success',
  PAYMENT_FAILED: 'payment_failed',
  PAYMENT_CANCELLED: 'payment_cancelled',
} as const;

export const ANALYTICS_CATEGORY = {
  NAVIGATION: 'navigation',
  AUTHENTICATION: 'authentication',
  ECOMMERCE: 'ecommerce',
  ENGAGEMENT: 'engagement',
  BOOK_INTERACTION: 'book_interaction',
  PAYMENT: 'payment',
} as const;

export type AnalyticsEvent = typeof ANALYTICS_EVENT[keyof typeof ANALYTICS_EVENT] | string;
export type AnalyticsCategory = typeof ANALYTICS_CATEGORY[keyof typeof ANALYTICS_CATEGORY] | string;

// Google Analytics tracking
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export class AnalyticsService {
  private static instance: AnalyticsService;
  private sessionId: string;
  private isInitialized = false;
  private measurementId: string | null = null;

  private constructor() {
    this.sessionId = this.generateSessionId();
  }

  static getInstance(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  // Check if analytics is ready (browser environment with GA loaded)
  isReady = (): boolean => {
    return isBrowser && this.isInitialized && typeof window.gtag === 'function';
  };

  // Initialize Google Analytics (arrow function for auto-binding)
  initializeGoogleAnalytics = (measurementId: string): void => {
    if (!isBrowser || this.isInitialized || !measurementId) return;

    // Load Google Analytics script
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script1);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      page_title: document.title,
      page_location: window.location.href,
    });

    this.measurementId = measurementId;
    this.isInitialized = true;
    logger.log('Google Analytics initialized with ID:', measurementId);
  };

  // Generate or retrieve session ID from sessionStorage
  private generateSessionId(): string {
    if (!isBrowser) {
      return `ssr_session_${Date.now()}`;
    }
    
    const storageKey = 'analytics_session_id';
    const existingSessionId = sessionStorage.getItem(storageKey);
    
    if (existingSessionId) {
      return existingSessionId;
    }
    
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem(storageKey, newSessionId);
    return newSessionId;
  }

  // Track page views (no-op safe, arrow function for auto-binding)
  trackPageView = async (page: string, title?: string): Promise<void> => {
    try {
      // Google Analytics - use 'event' for SPA navigation (GA4 recommended)
      if (this.isReady()) {
        window.gtag('event', 'page_view', {
          page_title: title || document.title,
          page_location: window.location.href,
        });
      }

      // Internal tracking (always attempt, has its own error handling)
      await this.trackEvent('page_view', 'navigation', {
        page,
        title: isBrowser ? title || document.title : title,
      });
    } catch (error) {
      // Silently fail - analytics should never break the app
      logger.warn('Analytics trackPageView failed:', { error, page });
    }
  };

  // Track custom events (no-op safe, arrow function for auto-binding)
  trackEvent = async (
    eventName: AnalyticsEvent,
    category: AnalyticsCategory,
    data?: Record<string, any>,
    value?: number,
    skipGA: boolean = false
  ): Promise<void> => {
    try {
      // Google Analytics - use flat parameters for GA4 compatibility
      if (!skipGA && this.isReady()) {
        window.gtag('event', eventName, {
          event_category: category,
          ...(data || {}),
          ...(value !== undefined && { value }),
        });
      }

      // Internal tracking to Supabase with fallback
      // Pass event_data as a JSON object (jsonb column) — must match the
      // fallback insert path below so analytics rows have consistent shape.
      const { error } = await supabase.rpc('track_event', {
        p_event_name: eventName,
        p_event_category: category,
        p_event_data: (data ?? null) as never,
        p_page_url: isBrowser ? window.location.href : null,
        p_referrer: isBrowser ? document.referrer : null,
        p_session_id: this.sessionId,
      });
      
      // Fallback to direct insert if RPC fails
      if (error) {
        logger.warn('RPC track_event failed, using fallback:', { error: error.message, eventName });
        const { error: insertError } = await supabase.from('analytics_events').insert({
          event_name: eventName,
          event_category: category,
          event_data: data || null,
          page_url: isBrowser ? window.location.href : null,
          referrer: isBrowser ? document.referrer : null,
          session_id: this.sessionId,
        });
        if (insertError) {
          logger.error('Analytics fallback insert failed:', { error: insertError.message, eventName });
        }
      }
    } catch (error) {
      // Silently fail - analytics should never break the app
      logger.warn('Analytics trackEvent failed:', { error, eventName, category });
    }
  };

  // Track user login (no-op safe, arrow function for auto-binding)
  trackLogin = async (method: string = 'email'): Promise<void> => {
    try {
      await this.trackEvent('login', 'authentication', { method });
      
      // Start user session with fallback
      const { error } = await supabase.rpc('start_user_session', {
        p_ip_address: null,
        p_user_agent: isBrowser ? navigator.userAgent : null,
        p_login_method: method,
      });
      
      // Fallback to direct insert if RPC fails
      if (error) {
        logger.warn('RPC start_user_session failed, using fallback:', { error: error.message, method });
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { error: insertError } = await supabase.from('user_sessions').insert({
            user_id: user.id,
            user_agent: isBrowser ? navigator.userAgent : null,
            login_method: method,
            is_active: true,
          });
          if (insertError) {
            logger.error('Session fallback insert failed:', { error: insertError.message, method });
          }
        }
      }
    } catch (error) {
      // Silently fail - analytics should never break the app
      logger.warn('Analytics trackLogin failed:', { error, method });
    }
  };

  // Track user logout (no-op safe, arrow function for auto-binding)
  trackLogout = async (): Promise<void> => {
    try {
      await this.trackEvent('logout', 'authentication');
      
      // End user session with fallback
      const { error } = await supabase.rpc('end_user_session');
      
      // Fallback to direct update if RPC fails
      if (error) {
        logger.warn('RPC end_user_session failed, using fallback:', { error: error.message });
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { error: updateError } = await supabase
            .from('user_sessions')
            .update({ session_end: new Date().toISOString(), is_active: false })
            .eq('user_id', user.id)
            .eq('is_active', true);
          if (updateError) {
            logger.error('Session fallback update failed:', { error: updateError.message });
          }
        }
      }
    } catch (error) {
      // Silently fail - analytics should never break the app
      logger.warn('Analytics trackLogout failed:', { error });
    }
  };

  // Track conversions (no-op safe, arrow function for auto-binding)
  trackConversion = async (type: string, value: number, currency: string = 'INR', orderId?: string): Promise<void> => {
    try {
      // Use provided orderId for reconciliation, or generate a unique ID
      const transactionId = orderId || (isBrowser && crypto?.randomUUID ? crypto.randomUUID() : `txn_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`);
      // Google Analytics Enhanced Ecommerce
      if (this.isReady()) {
        window.gtag('event', 'purchase', {
          transaction_id: transactionId,
          value: value,
          currency: currency,
          event_category: 'ecommerce',
        });
      }

      // Use 'purchase' for consistency with GA - skip GA since we already sent it above
      await this.trackEvent('purchase', 'ecommerce', {
        transaction_id: transactionId,
        type,
        value,
        currency,
      }, undefined, true);
    } catch (error) {
      // Silently fail - analytics should never break the app
      logger.warn('Analytics trackConversion failed:', { error, type, value });
    }
  };

  // Track book interactions (no-op safe, arrow function for auto-binding)
  trackBookInteraction = async (action: string, bookId: string, bookTitle: string): Promise<void> => {
    try {
      await this.trackEvent(action, 'book_interaction', {
        book_id: bookId,
        book_title: bookTitle,
      });
    } catch (error) {
      // Silently fail - analytics should never break the app
      logger.warn('Analytics trackBookInteraction failed:', { error, action, bookId });
    }
  };

  // Track search (no-op safe, arrow function for auto-binding)
  trackSearch = async (query: string, results: number): Promise<void> => {
    try {
      if (this.isReady()) {
        window.gtag('event', 'search', {
          search_term: query,
          event_category: 'engagement',
        });
      }

      // Skip GA since we already sent the search event above
      await this.trackEvent('search', 'engagement', {
        query,
        results_count: results,
      }, undefined, true);
    } catch (error) {
      // Silently fail - analytics should never break the app
      logger.warn('Analytics trackSearch failed:', { error, query });
    }
  };
}

export const analytics = AnalyticsService.getInstance();
